import { Button, Modal, useToast } from '@orbind/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.ts'

/**
 * Depois de mudar a empresa de um item que TEM filhos (SPEC-20261006-1027-empresa-desce-para-os-filhos):
 * a mudança vale só para ele, ou desce para a árvore inteira?
 *
 * As duas respostas são legítimas. Um item filho costuma ser trabalho da mesma
 * empresa do pai — por isso ele já nasce com ela —, mas uma árvore pode ter
 * ramos de empresas diferentes, e descer sozinho mudaria o que ninguém mandou
 * mudar. Por isso se pergunta, em vez de decidir.
 *
 * A empresa do próprio item JÁ está gravada quando esta janela abre: fechar
 * sem descer não desfaz nada, só deixa os filhos como estavam.
 */
export function DescerEmpresaModal({
  itemId,
  filhos,
  empresa,
  onFechar,
}: {
  itemId: string
  filhos: number
  empresa: string
  onFechar: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const descer = useMutation({
    mutationFn: async () =>
      (await api['work-items'][':id']['empresa-em-cascata'].$post({ param: { id: itemId } })).json(),
    onSuccess: (dados) => {
      void queryClient.invalidateQueries({ queryKey: ['work-item'] })
      void queryClient.invalidateQueries({ queryKey: ['work-items'] })
      toast.push({
        tone: 'success',
        message: `Empresa aplicada a ${dados.alterados} ${dados.alterados === 1 ? 'item' : 'itens'}.`,
      })
      onFechar()
    },
    onError: (e) =>
      toast.push({
        tone: 'danger',
        message: e instanceof Error ? e.message : 'Não foi possível alterar os filhos.',
      }),
  })

  return (
    <Modal
      open
      onClose={onFechar}
      title="Alterar também os itens filhos?"
      footer={
        <>
          <Button variant="ghost" onClick={onFechar} disabled={descer.isPending}>
            Somente este item
          </Button>
          <Button onClick={() => descer.mutate()} disabled={descer.isPending}>
            {descer.isPending
              ? 'Alterando…'
              : filhos === 1
                ? 'Alterar o filho'
                : `Alterar os ${filhos} filhos`}
          </Button>
        </>
      }
    >
      <p style={{ fontFamily: 'var(--ob-font-body)', fontSize: 13, margin: 0 }}>
        Este item passou a ser de <strong>{empresa}</strong>. Ele tem{' '}
        <strong>
          {filhos} {filhos === 1 ? 'item filho' : 'itens filhos'}
        </strong>{' '}
        na árvore abaixo dele.
      </p>
      <p
        style={{
          fontFamily: 'var(--ob-font-body)',
          fontSize: 12,
          color: 'var(--ob-text-muted)',
          margin: '10px 0 0',
        }}
      >
        A empresa deste item já foi gravada. Escolher “somente este item” apenas deixa os
        filhos como estavam.
      </p>
    </Modal>
  )
}
